/**
 * Organization billing, system admin only. Everything here lives under `/api/admin/billing`.
 *
 * **Every response is normalised rather than cast.** Web and the API deploy on separate workflows
 * off the same push (see the root CLAUDE.md), so a new build regularly meets an older server for a
 * few minutes. A cast would turn a missing field into `undefined` reaching a `.toLocaleString()`
 * deep inside a table; normalising at the boundary means the worst case is a dash in a cell.
 *
 * The same reasoning applies to the string enums: an unrecognised value is collapsed to the safest
 * one this build understands rather than printed raw, so a status the client has never heard of
 * never appears on screen as a bare identifier.
 */

import { del, get, post, put } from './api';
import type {
  BillingCollectionMethod,
  BillingContact,
  BillingContactKind,
  BillingEmailTemplate,
  BillingEmailTemplateField,
  BillingEmailTemplatePreview,
  BillingEmailTemplateSummary,
  BillingInterval,
  BillingInvoice,
  BillingInvoiceDisplayStatus,
  BillingInvoiceSource,
  BillingInvoiceStatus,
  BillingOrganizationListItem,
  BillingOrganizationListResponse,
  BillingOrganizationSummary,
  BillingPaymentMethod,
  BillingPaymentMethodKind,
  BillingPlan,
  BillingPlanStatus,
  BillingReminder,
  BillingReminderKind,
  BillingReminderOutcome,
  BillingReminderSource,
  BillingSettings,
  BillingStripeBackfill,
  BillingStripeCustomer,
  BillingStripeCustomerSearchItem,
  BillingStripePaymentMethod,
  BillingStripeSetupIntent,
  BillingStripeSetupKind,
  BillingStripeSetupState,
  BillingStripeSubscription,
  CreateBillingInvoiceRequest,
  CreateStripeCustomerRequest,
  CreateStripeSubscriptionRequest,
  GetBillingOrganizationsParams,
  MarkInvoicePaidRequest,
  PreviewBillingEmailTemplateRequest,
  SaveBillingContactRequest,
  SaveBillingEmailTemplateRequest,
  ReverseInvoicePaymentRequest,
  SaveBillingPlanRequest,
} from '../types/adminBilling';

const BASE_URL = '/api/admin/billing';

/**
 * `apiRequest` only wires its AbortController when a timeout is supplied, so without this a stalled
 * write leaves a dialog on "Saving..." with no way back. Same value and same reasoning as the
 * pause/resume calls in `admin.ts`.
 */
const WRITE_TIMEOUT_MS = 15000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const nullableStr = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const bool = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const nullableNum = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const array = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** A collection that may arrive bare or wrapped in `{ items }`, depending on the endpoint. */
const collection = (raw: unknown): unknown[] =>
  Array.isArray(raw) ? raw : isRecord(raw) ? array(raw.items) : [];

const stringList = (value: unknown): string[] =>
  array(value).filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);

const oneOf = <T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T => (allowed.includes(value as T) ? (value as T) : fallback);

const oneOfOrNull = <T extends string>(value: unknown, allowed: readonly T[]): T | null =>
  allowed.includes(value as T) ? (value as T) : null;

const INTERVALS: readonly BillingInterval[] = ['Monthly', 'Annual'];
const PLAN_STATUSES: readonly BillingPlanStatus[] = ['Active', 'Paused', 'Cancelled'];
const CONTACT_KINDS: readonly BillingContactKind[] = ['To', 'Cc'];
const PAYMENT_METHOD_KINDS: readonly BillingPaymentMethodKind[] = ['None', 'Card', 'Ach'];
const PAYMENT_METHODS: readonly BillingPaymentMethod[] = ['Manual', 'Card', 'Ach', 'Stripe'];
const INVOICE_STATUSES: readonly BillingInvoiceStatus[] = [
  'Open',
  'Draft',
  'Paid',
  'Void',
  'Uncollectible',
];
const DISPLAY_STATUSES: readonly BillingInvoiceDisplayStatus[] = [
  'open',
  'draft',
  'overdue',
  'paid',
  'void',
  'uncollectible',
];
const INVOICE_SOURCES: readonly BillingInvoiceSource[] = ['Local', 'Stripe'];
const REMINDER_KINDS: readonly BillingReminderKind[] = [
  'BeforeDue',
  'DueToday',
  'Overdue',
  'StripeSent',
  'StripePaymentFailed',
];
const REMINDER_OUTCOMES: readonly BillingReminderOutcome[] = [
  'sent',
  'suppressed',
  'failed',
  'pending',
];
const REMINDER_SOURCES: readonly BillingReminderSource[] = ['RosterPilot', 'Stripe'];
const COLLECTION_METHODS: readonly BillingCollectionMethod[] = [
  'ChargeAutomatically',
  'SendInvoice',
];

const DEFAULT_CURRENCY = 'USD';

const normalizePlan = (raw: unknown): BillingPlan | null => {
  if (!isRecord(raw)) return null;
  return {
    amountCents: num(raw.amountCents),
    currency: str(raw.currency, DEFAULT_CURRENCY) || DEFAULT_CURRENCY,
    interval: oneOf(raw.interval, INTERVALS, 'Monthly'),
    paymentTermsDays: num(raw.paymentTermsDays),
    firstInvoiceOn: str(raw.firstInvoiceOn),
    nextInvoiceOn: nullableStr(raw.nextInvoiceOn),
    status: oneOf(raw.status, PLAN_STATUSES, 'Active'),
    notes: str(raw.notes),
    defaultPaymentMethodKind: oneOf(raw.defaultPaymentMethodKind, PAYMENT_METHOD_KINDS, 'None'),
    defaultPaymentMethodLabel: nullableStr(raw.defaultPaymentMethodLabel),
    hasStripeCustomer: bool(raw.hasStripeCustomer),
    /*
      Defaults to false, and falls back to `hasStripeCustomer` rather than to nothing: a server old
      enough not to send the newer field still answers the same question with the older one, and
      both absent means "this system bills them", which is what every organization was before Stripe
      existed.
    */
    isStripeManaged: bool(raw.isStripeManaged, bool(raw.hasStripeCustomer)),
    stripeCustomerId: nullableStr(raw.stripeCustomerId),
    stripeInvoicesSyncedAt: nullableStr(raw.stripeInvoicesSyncedAt),
    /*
      The cached subscription hint. All four are null on a server that has never heard of them,
      which reads as "we last heard nothing" — the same thing a screen shows before the live read
      answers, and never as a subscription that does not exist.

      `stripeCollectionMethod` is narrowed to null rather than passed through, because it is the
      one of the four a screen could act on: an unrecognised word must not be read as
      `ChargeAutomatically`.
    */
    stripeSubscriptionId: nullableStr(raw.stripeSubscriptionId),
    stripeSubscriptionStatus: nullableStr(raw.stripeSubscriptionStatus),
    stripeCollectionMethod: oneOfOrNull(raw.stripeCollectionMethod, COLLECTION_METHODS),
    stripeSubscriptionNextInvoiceAt: nullableStr(raw.stripeSubscriptionNextInvoiceAt),
  };
};

/**
 * `displayStatus` is taken as sent and only falls back when it is missing or unrecognised, and
 * even then it falls back to the *stored* status rather than to a date comparison. An older server
 * that does not send it shows "open" on an overdue invoice, which is merely incomplete; a client
 * that re-derived it would show a confident answer the server disagrees with.
 */
const displayStatusFor = (raw: Record<string, unknown>, status: BillingInvoiceStatus) => {
  const sent = oneOfOrNull(raw.displayStatus, DISPLAY_STATUSES);
  if (sent) return sent;
  return status.toLowerCase() as BillingInvoiceDisplayStatus;
};

const normalizeInvoice = (raw: unknown): BillingInvoice | null => {
  if (!isRecord(raw)) return null;
  const id = nullableStr(raw.id);
  if (!id) return null;
  const status = oneOf(raw.status, INVOICE_STATUSES, 'Open');
  return {
    id,
    invoiceNumber: nullableStr(raw.number),
    amountCents: num(raw.amountCents),
    currency: str(raw.currency, DEFAULT_CURRENCY) || DEFAULT_CURRENCY,
    status,
    displayStatus: displayStatusFor(raw, status),
    isOverdue: bool(raw.isOverdue),
    daysOverdue: num(raw.daysOverdue),
    issuedOn: nullableStr(raw.issuedOn),
    dueOn: nullableStr(raw.dueOn),
    periodStart: nullableStr(raw.periodStart),
    periodEnd: nullableStr(raw.periodEnd),
    paidAt: nullableStr(raw.paidAt),
    paidAmountCents: nullableNum(raw.paidAmountCents),
    paymentMethod: oneOfOrNull(raw.paidMethod, PAYMENT_METHODS),
    voidReason: nullableStr(raw.voidReason),
    /*
      All three arrive together or not at all, and all three are absent on a server predating the
      reversal endpoint. Null reads as "nothing was ever taken back", which is what that server
      meant, so the row simply does not mention a reversal rather than implying one.
    */
    reversedAt: nullableStr(raw.reversedAt),
    reversedAmountCents: nullableNum(raw.reversedAmountCents),
    reversalReason: nullableStr(raw.reversalReason),
    createdAt: nullableStr(raw.createdAt),
    /*
      `source` falls back to the older `hasStripeInvoice` flag and then to Local. It only decides
      what the row SAYS; what the row may DO is `canSettleLocally` and nothing else.
    */
    source: oneOfOrNull(raw.source, INVOICE_SOURCES) ?? (bool(raw.hasStripeInvoice) ? 'Stripe' : 'Local'),
    /*
      **False unless the server says otherwise.** This is the one field whose absence must not be
      read as permission: an older API that has never heard of the mirror would otherwise hand a
      new build a mark-paid button for every mirrored invoice, and each press would answer 409 after
      the admin had already told the college it was settled.
    */
    canSettleLocally: bool(raw.canSettleLocally),
    stripeStatus: nullableStr(raw.stripeStatus),
    amountRemainingCents: nullableNum(raw.amountRemainingCents),
    hostedInvoiceUrl: nullableStr(raw.hostedInvoiceUrl),
    invoicePdfUrl: nullableStr(raw.invoicePdfUrl),
  };
};

const normalizeReminder = (raw: unknown): BillingReminder | null => {
  if (!isRecord(raw)) return null;
  const id = nullableStr(raw.id);
  if (!id) return null;
  const sentAt = nullableStr(raw.sentAt);
  const failureReason = nullableStr(raw.failureReason);
  const suppressedReason = nullableStr(raw.suppressedReason);
  /*
    The outcome is the server's to state. It is only inferred when the field is missing entirely,
    and then from the three facts that cannot contradict it: a failure reason, a suppression
    reason, or a send time.
  */
  const outcome =
    oneOfOrNull(raw.outcome, REMINDER_OUTCOMES) ??
    (failureReason
      ? 'failed'
      : suppressedReason
        ? 'suppressed'
        : sentAt
          ? 'sent'
          : 'pending');
  return {
    id,
    kind: oneOf(raw.kind, REMINDER_KINDS, 'Overdue'),
    anchor: nullableStr(raw.anchor),
    claimedAt: nullableStr(raw.claimedAt),
    sentAt,
    toAddresses: stringList(raw.toAddresses),
    ccAddresses: stringList(raw.ccAddresses),
    templateKey: nullableStr(raw.templateKey),
    failureReason,
    suppressedReason,
    outcome,
    // Defaults to RosterPilot: a server that does not send it has no Stripe rows to confuse this
    // with, and every row it can return is one this system sent.
    source: oneOf(raw.source, REMINDER_SOURCES, 'RosterPilot'),
  };
};

const normalizeContact = (raw: unknown): BillingContact | null => {
  if (!isRecord(raw)) return null;
  const id = nullableStr(raw.id);
  if (!id) return null;
  return {
    id,
    email: str(raw.email),
    name: nullableStr(raw.name),
    kind: oneOf(raw.kind, CONTACT_KINDS, 'To'),
    // Absent means active: a contact the server sent back but did not describe is more likely to
    // be live than silently switched off, and the row says which either way.
    isActive: bool(raw.isActive, true),
  };
};

const normalizeListItem = (raw: unknown): BillingOrganizationListItem | null => {
  if (!isRecord(raw)) return null;
  const organizationId = nullableStr(raw.organizationId);
  if (!organizationId) return null;
  return {
    organizationId,
    organizationName: str(raw.organizationName, 'Unnamed organization'),
    amountCents: nullableNum(raw.amountCents),
    currency: nullableStr(raw.currency),
    interval: oneOfOrNull(raw.interval, INTERVALS),
    planStatus: oneOfOrNull(raw.planStatus, PLAN_STATUSES),
    nextInvoiceOn: nullableStr(raw.nextInvoiceOn),
    outstandingCents: num(raw.outstandingCents),
    overdueInvoiceCount: num(raw.overdueInvoiceCount),
    oldestDueOn: nullableStr(raw.oldestDueOn),
    /*
      Floored at zero. A negative reading would be an invoice due in the future described as days
      past due, which is the one thing this number must never say.
    */
    oldestInvoiceDaysOverdue: Math.max(0, num(raw.oldestInvoiceDaysOverdue)),
    /*
      Defaults to false, which understates rather than overstates: an older server's silence shows
      a paused organization as running, and the row still links to the screen that says otherwise.
      The reverse would mark live organizations as suspended across the whole list.
    */
    isPaused: bool(raw.isPaused),
    lastReminderAt: nullableStr(raw.lastReminderAt),
    /*
      Defaults to false. The flag is a warning, and an older server that cannot answer the question
      must not have its silence rendered as "this organization is unreachable" against every row.
    */
    hasNoDeliverableContact: bool(raw.hasNoDeliverableContact),
    isStripeManaged: bool(raw.isStripeManaged),
    // Same reasoning as the contact warning above: an older server that cannot answer must not have
    // its silence rendered as "nobody is billing this organization" against every row.
    hasNoBillingSource: bool(raw.hasNoBillingSource),
  };
};

const normalizeSummary = (raw: unknown, orgId: string): BillingOrganizationSummary => {
  const record = isRecord(raw) ? raw : {};
  const plan = normalizePlan(record.plan);
  return {
    organizationId: str(record.organizationId, orgId) || orgId,
    organizationName: str(record.organizationName, 'Unnamed organization'),
    plan,
    nextInvoiceOn: nullableStr(record.nextInvoiceOn),
    latestInvoice: normalizeInvoice(record.latestInvoice),
    outstandingCents: num(record.outstandingCents),
    outstandingInvoiceCount: num(record.outstandingInvoiceCount),
    overdueInvoiceCount: num(record.overdueInvoiceCount),
    overdueCents: num(record.overdueCents),
    oldestDueOn: nullableStr(record.oldestDueOn),
    oldestInvoiceDaysOverdue: Math.max(0, num(record.oldestInvoiceDaysOverdue)),
    isPaused: bool(record.isPaused),
    activeToContactCount: num(record.activeToContactCount),
    activeCcContactCount: num(record.activeCcContactCount),
    lastReminder: normalizeReminder(record.lastReminder),
    // The plan is the second opinion, not a different one: the API derives both from the same
    // customer id, so a server that sends the plan but not the summary flag still answers.
    isStripeManaged: bool(record.isStripeManaged, plan?.isStripeManaged ?? false),
  };
};

const DEFAULT_SETTINGS: BillingSettings = {
  remindersEnabled: false,
  stripeEnabled: false,
  daysBeforeDue: [],
  overdueEveryDays: 0,
};

const normalizeSettings = (raw: unknown): BillingSettings => {
  if (!isRecord(raw)) return { ...DEFAULT_SETTINGS };
  return {
    remindersEnabled: bool(raw.remindersEnabled),
    stripeEnabled: bool(raw.stripeEnabled),
    daysBeforeDue: array(raw.daysBeforeDue)
      .filter((entry): entry is number => typeof entry === 'number' && Number.isFinite(entry))
      .map((entry) => Math.trunc(entry)),
    overdueEveryDays: num(raw.overdueEveryDays),
  };
};

const notNull = <T>(value: T | null): value is T => value !== null;

const orgUrl = (orgId: string) => `${BASE_URL}/organizations/${encodeURIComponent(orgId)}`;
const invoiceUrl = (invoiceId: string) => `${BASE_URL}/invoices/${encodeURIComponent(invoiceId)}`;

export const getBillingOrganizations = async (
  params: GetBillingOrganizationsParams = {},
  signal?: AbortSignal
): Promise<BillingOrganizationListResponse> => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  if (params.search) query.set('search', params.search);
  if (params.overdueOnly) query.set('overdueOnly', 'true');

  const qs = query.toString();
  const raw = await get<unknown>(`${BASE_URL}/organizations${qs ? `?${qs}` : ''}`, { signal });
  const record = isRecord(raw) ? raw : {};
  const items = collection(record.items).map(normalizeListItem).filter(notNull);

  return {
    items,
    // Falling back to the number of rows actually returned keeps the pager honest on a server that
    // does not send a count, rather than showing "0 of 0" over a full page of organizations.
    totalCount: num(record.totalCount, items.length),
    page: num(record.page, params.page ?? 1),
    pageSize: num(record.pageSize, params.pageSize ?? items.length),
    totalOutstandingCents: num(record.totalOutstandingCents),
  };
};

export const getBillingOrganizationSummary = async (
  orgId: string,
  signal?: AbortSignal
): Promise<BillingOrganizationSummary> =>
  normalizeSummary(await get<unknown>(orgUrl(orgId), { signal }), orgId);

export const getBillingPlan = async (
  orgId: string,
  signal?: AbortSignal
): Promise<BillingPlan | null> => normalizePlan(await get<unknown>(`${orgUrl(orgId)}/plan`, { signal }));

/** `nextInvoiceOn` is never sent: the API derives it from the interval and what it has invoiced. */
export const saveBillingPlan = async (
  orgId: string,
  request: SaveBillingPlanRequest
): Promise<BillingPlan | null> =>
  normalizePlan(
    await put<unknown>(`${orgUrl(orgId)}/plan`, request, { timeout: WRITE_TIMEOUT_MS })
  );

export const getBillingContacts = async (
  orgId: string,
  signal?: AbortSignal
): Promise<BillingContact[]> =>
  collection(await get<unknown>(`${orgUrl(orgId)}/contacts`, { signal }))
    .map(normalizeContact)
    .filter(notNull);

export const createBillingContact = async (
  orgId: string,
  request: SaveBillingContactRequest
): Promise<BillingContact | null> =>
  normalizeContact(
    await post<unknown>(`${orgUrl(orgId)}/contacts`, request, { timeout: WRITE_TIMEOUT_MS })
  );

export const updateBillingContact = async (
  orgId: string,
  contactId: string,
  request: SaveBillingContactRequest
): Promise<BillingContact | null> =>
  normalizeContact(
    await put<unknown>(
      `${orgUrl(orgId)}/contacts/${encodeURIComponent(contactId)}`,
      request,
      { timeout: WRITE_TIMEOUT_MS }
    )
  );

/**
 * A soft deactivate, not a delete. The contact stays on the organization and an edit can set
 * `isActive` back to true, which is why the UI says "Deactivate" rather than "Remove".
 */
export const deactivateBillingContact = (orgId: string, contactId: string): Promise<unknown> =>
  del<unknown>(`${orgUrl(orgId)}/contacts/${encodeURIComponent(contactId)}`, {
    timeout: WRITE_TIMEOUT_MS,
  });

export const getBillingInvoices = async (
  orgId: string,
  signal?: AbortSignal
): Promise<BillingInvoice[]> =>
  collection(await get<unknown>(`${orgUrl(orgId)}/invoices`, { signal }))
    .map(normalizeInvoice)
    .filter(notNull);

export const createBillingInvoice = async (
  orgId: string,
  request: CreateBillingInvoiceRequest
): Promise<BillingInvoice | null> =>
  normalizeInvoice(
    await post<unknown>(`${orgUrl(orgId)}/invoices`, request, { timeout: WRITE_TIMEOUT_MS })
  );

export const markInvoicePaid = async (
  invoiceId: string,
  request: MarkInvoicePaidRequest = {}
): Promise<BillingInvoice | null> =>
  normalizeInvoice(
    await post<unknown>(`${invoiceUrl(invoiceId)}/mark-paid`, request, {
      timeout: WRITE_TIMEOUT_MS,
    })
  );

export const voidInvoice = async (
  invoiceId: string,
  reason?: string | null
): Promise<BillingInvoice | null> =>
  normalizeInvoice(
    await post<unknown>(
      `${invoiceUrl(invoiceId)}/void`,
      { reason: reason && reason.trim() ? reason.trim() : null },
      { timeout: WRITE_TIMEOUT_MS }
    )
  );

/**
 * Takes back money recorded against an invoice, reopening it or writing it off.
 *
 * **Refused with a 409 on a mirrored invoice**, like every other write: Stripe holds those, and a
 * refund there belongs in the Stripe dashboard. The reason is sent trimmed and is required, so an
 * empty one never reaches the API to come back as a 400 the admin has to read.
 */
export const reverseInvoicePayment = async (
  invoiceId: string,
  request: ReverseInvoicePaymentRequest
): Promise<BillingInvoice | null> =>
  normalizeInvoice(
    await post<unknown>(
      `${invoiceUrl(invoiceId)}/reverse-payment`,
      {
        reason: request.reason.trim(),
        ...(typeof request.amountCents === 'number' ? { amountCents: request.amountCents } : {}),
        uncollectible: request.uncollectible === true,
      },
      { timeout: WRITE_TIMEOUT_MS }
    )
  );

export const getInvoiceReminders = async (
  invoiceId: string,
  signal?: AbortSignal
): Promise<BillingReminder[]> =>
  collection(await get<unknown>(`${invoiceUrl(invoiceId)}/reminders`, { signal }))
    .map(normalizeReminder)
    .filter(notNull);

export const getBillingSettings = async (signal?: AbortSignal): Promise<BillingSettings> =>
  normalizeSettings(await get<unknown>(`${BASE_URL}/settings`, { signal }));

/**
 * Sends all four fields, `stripeEnabled` included.
 *
 * The screen does not let an admin change the Stripe flag, but omitting it from the body would let
 * a server that treats a missing boolean as `false` switch it off as a side effect of saving the
 * reminder schedule. Echoing back what was loaded is the only version of this call that cannot
 * change something nobody touched.
 */
export const saveBillingSettings = async (settings: BillingSettings): Promise<BillingSettings> =>
  normalizeSettings(
    await put<unknown>(
      `${BASE_URL}/settings`,
      {
        remindersEnabled: settings.remindersEnabled,
        stripeEnabled: settings.stripeEnabled,
        daysBeforeDue: settings.daysBeforeDue,
        overdueEveryDays: settings.overdueEveryDays,
      },
      { timeout: WRITE_TIMEOUT_MS }
    )
  );

/* ------------------------------------------------------- reminder email templates */

/**
 * A template key is `billing.reminder.overdue` — dots and all — and it is the last segment of the
 * URL. `encodeURIComponent` is what every other keyed admin route uses (see `admin-tours.ts`) and
 * is what keeps a key with a slash or a space in it from becoming two segments.
 */
const templateUrl = (key: string) => `${BASE_URL}/templates/${encodeURIComponent(key)}`;

const normalizeTemplateField = (raw: unknown): BillingEmailTemplateField | null => {
  if (!isRecord(raw)) return null;
  const name = nullableStr(raw.name);
  if (!name) return null;
  return {
    name,
    description: str(raw.description),
    sampleValue: str(raw.sampleValue),
  };
};

const normalizeTemplateSummary = (raw: unknown): BillingEmailTemplateSummary | null => {
  if (!isRecord(raw)) return null;
  const key = nullableStr(raw.key);
  if (!key) return null;
  return {
    key,
    name: str(raw.name, key) || key,
    description: str(raw.description),
    subject: str(raw.subject),
    // Defaults to false: an older server that cannot answer must not have its silence rendered as
    // "this copy has been customised" against every row.
    isCustomised: bool(raw.isCustomised),
    updatedAt: nullableStr(raw.updatedAt),
  };
};

const normalizeTemplate = (raw: unknown, key: string): BillingEmailTemplate => {
  const record = isRecord(raw) ? raw : {};
  return {
    key: str(record.key, key) || key,
    name: str(record.name, key) || key,
    description: str(record.description),
    subject: str(record.subject),
    bodyHtml: str(record.bodyHtml),
    bodyText: str(record.bodyText),
    isCustomised: bool(record.isCustomised),
    defaultSubject: str(record.defaultSubject),
    defaultBodyHtml: str(record.defaultBodyHtml),
    defaultBodyText: str(record.defaultBodyText),
    fields: array(record.fields).map(normalizeTemplateField).filter(notNull),
    updatedAt: nullableStr(record.updatedAt),
  };
};

const normalizeTemplatePreview = (raw: unknown, key: string): BillingEmailTemplatePreview => {
  const record = isRecord(raw) ? raw : {};
  return {
    key: str(record.key, key) || key,
    subject: str(record.subject),
    html: str(record.html),
    plainText: str(record.plainText),
    sample: array(record.sample).map(normalizeTemplateField).filter(notNull),
  };
};

/** Every billing email, with whether its copy has been customised. Subjects only, no bodies. */
export const getBillingEmailTemplates = async (
  signal?: AbortSignal
): Promise<BillingEmailTemplateSummary[]> =>
  collection(await get<unknown>(`${BASE_URL}/templates`, { signal }))
    .map(normalizeTemplateSummary)
    .filter(notNull);

export const getBillingEmailTemplate = async (
  key: string,
  signal?: AbortSignal
): Promise<BillingEmailTemplate> =>
  normalizeTemplate(await get<unknown>(templateUrl(key), { signal }), key);

/**
 * Replaces one template's copy. All three fields every time — the API treats this as a PUT, and a
 * subject saved without its bodies would be half an email.
 *
 * Copy that does not render comes back as a 400 whose message names the problem. It is the whole
 * point of this call failing loudly: the next thing to meet a broken template would be the morning
 * reminder sweep, recording a failure against a customer's invoice with nobody watching.
 */
export const saveBillingEmailTemplate = async (
  key: string,
  request: SaveBillingEmailTemplateRequest
): Promise<BillingEmailTemplate> =>
  normalizeTemplate(
    await put<unknown>(templateUrl(key), request, { timeout: WRITE_TIMEOUT_MS }),
    key
  );

/**
 * Renders a template against a sample invoice and **sends nothing**.
 *
 * Fields left out fall back to the copy in force, so the same call previews an unsaved edit and
 * previews what customers receive today.
 */
export const previewBillingEmailTemplate = async (
  key: string,
  request: PreviewBillingEmailTemplateRequest = {}
): Promise<BillingEmailTemplatePreview> =>
  normalizeTemplatePreview(
    await post<unknown>(`${templateUrl(key)}/preview`, request, { timeout: WRITE_TIMEOUT_MS }),
    key
  );

/**
 * Drops the customised copy so the template bundled with the API is used again. Idempotent, and
 * answers with the template so the editor can render what it fell back to.
 */
export const resetBillingEmailTemplate = async (key: string): Promise<BillingEmailTemplate> =>
  normalizeTemplate(await del<unknown>(templateUrl(key), { timeout: WRITE_TIMEOUT_MS }), key);

/* -------------------------------------------------------------------------- stripe */

const SETUP_STATES: readonly BillingStripeSetupState[] = [
  'None',
  'AwaitingCustomer',
  'PendingMicrodeposits',
  'Failed',
];

const SETUP_KINDS: readonly BillingStripeSetupKind[] = ['Card', 'Ach'];

const stripeOrgUrl = (orgId: string) => `${orgUrl(orgId)}/stripe`;

const normalizeStripeCustomer = (raw: unknown): BillingStripeCustomer => {
  const record = isRecord(raw) ? raw : {};
  return {
    customerId: str(record.customerId),
    created: bool(record.created),
    email: nullableStr(record.email),
    name: nullableStr(record.name),
    /*
      Empty rather than invented when the server says nothing. The screen falls back to its own
      sentence in that case; what it must never do is put words in the server's mouth about whether
      the details typed into the form were applied.
    */
    message: str(record.message),
  };
};

/**
 * The subscription as Stripe has it.
 *
 * **Every field that grants an action defaults to the safe value.** `hasSubscription` false shows
 * the create step rather than cancel controls, and `cancelAtPeriodEnd` false is "still billing",
 * which is what an operator must be shown before they decide anything. `status` is Stripe's own
 * word and is carried through untouched, because the screen prints it rather than branching on it:
 * a status this build has never heard of still reads as something.
 *
 * `collectionMethod` is the exception that is narrowed: it is a choice this app also *sends*, so an
 * unrecognised word collapses to null and the screen says it does not know rather than naming the
 * wrong one.
 */
const normalizeSubscription = (raw: unknown, orgId: string): BillingStripeSubscription => {
  const record = isRecord(raw) ? raw : {};
  const subscriptionId = nullableStr(record.subscriptionId);
  return {
    organizationId: str(record.organizationId, orgId) || orgId,
    /*
      Falls back to "is there an id", which is the same question asked of older data. It cannot
      manufacture a subscription out of nothing, and the cancel controls are gated on the id as
      well, so a server that answers neither can never produce a cancel button.
    */
    hasSubscription: bool(record.hasSubscription, subscriptionId !== null),
    isStripeManaged: bool(record.isStripeManaged, nullableStr(record.customerId) !== null),
    customerId: nullableStr(record.customerId),
    subscriptionId,
    status: nullableStr(record.status),
    collectionMethod: oneOfOrNull(record.collectionMethod, COLLECTION_METHODS),
    daysUntilDue: nullableNum(record.daysUntilDue),
    priceId: nullableStr(record.priceId),
    amountCents: nullableNum(record.amountCents),
    currency: nullableStr(record.currency),
    interval: oneOfOrNull(record.interval, INTERVALS),
    currentPeriodStart: nullableStr(record.currentPeriodStart),
    currentPeriodEnd: nullableStr(record.currentPeriodEnd),
    startsAt: nullableStr(record.startsAt),
    nextInvoiceAt: nullableStr(record.nextInvoiceAt),
    cancelAtPeriodEnd: bool(record.cancelAtPeriodEnd),
    cancelAt: nullableStr(record.cancelAt),
    canceledAt: nullableStr(record.canceledAt),
    message: str(record.message),
  };
};

const normalizeCustomerSearchItem = (raw: unknown): BillingStripeCustomerSearchItem | null => {
  if (!isRecord(raw)) return null;
  const customerId = nullableStr(raw.customerId);
  // A row with no id cannot be linked, and linking is the only thing this list is for.
  if (!customerId) return null;
  return {
    customerId,
    name: nullableStr(raw.name),
    email: nullableStr(raw.email),
    linkedOrganizationName: nullableStr(raw.linkedOrganizationName),
  };
};

const normalizeBackfill = (raw: unknown, orgId: string): BillingStripeBackfill => {
  const record = isRecord(raw) ? raw : {};
  return {
    organizationId: str(record.organizationId, orgId) || orgId,
    customerId: str(record.customerId),
    imported: num(record.imported),
    updated: num(record.updated),
    unchanged: num(record.unchanged),
    totalAtStripe: num(record.totalAtStripe),
    syncedAt: nullableStr(record.syncedAt),
    message: str(record.message),
  };
};

const normalizeSetupIntent = (
  raw: unknown,
  kind: BillingStripeSetupKind
): BillingStripeSetupIntent => {
  const record = isRecord(raw) ? raw : {};
  return {
    setupIntentId: str(record.setupIntentId),
    clientSecret: str(record.clientSecret),
    publishableKey: str(record.publishableKey),
    kind: oneOf(record.kind, SETUP_KINDS, kind),
    state: oneOf(record.state, SETUP_STATES, 'AwaitingCustomer'),
    hostedVerificationUrl: nullableStr(record.hostedVerificationUrl),
  };
};

/**
 * What is on file for an organization.
 *
 * **`isChargeable` defaults to false**, which is the only safe direction for this one field: a
 * server that cannot answer the question must not have its silence rendered as a charge button
 * over an ACH mandate that is still in microdeposit verification.
 *
 * An unrecognised `pendingSetupState` collapses to `None`, but `pendingSetupMessage` is carried
 * through untouched, so a state this build has never heard of still shows the server's sentence
 * rather than nothing at all.
 */
const normalizePaymentMethod = (raw: unknown, orgId: string): BillingStripePaymentMethod => {
  const record = isRecord(raw) ? raw : {};
  return {
    organizationId: str(record.organizationId, orgId) || orgId,
    hasCustomer: bool(record.hasCustomer),
    customerId: nullableStr(record.customerId),
    kind: oneOf(record.kind, PAYMENT_METHOD_KINDS, 'None'),
    paymentMethodId: nullableStr(record.paymentMethodId),
    label: nullableStr(record.label),
    isChargeable: bool(record.isChargeable),
    pendingSetupState: oneOf(record.pendingSetupState, SETUP_STATES, 'None'),
    pendingSetupIntentId: nullableStr(record.pendingSetupIntentId),
    pendingSetupKind: oneOf(record.pendingSetupKind, PAYMENT_METHOD_KINDS, 'None'),
    pendingSetupMessage: nullableStr(record.pendingSetupMessage),
  };
};

/** How many characters the lookup needs before it will answer at all. */
export const STRIPE_CUSTOMER_SEARCH_MIN_LENGTH = 3;

/**
 * Finds Stripe customers by name or email.
 *
 * **A lookup, not a browse.** Fewer than three characters is refused here rather than sent, because
 * the API answers an empty list either way and a request per keystroke from the first letter is a
 * request that can never return anything useful.
 */
export const searchStripeCustomers = async (
  search: string,
  signal?: AbortSignal
): Promise<BillingStripeCustomerSearchItem[]> => {
  const term = search.trim();
  if (term.length < STRIPE_CUSTOMER_SEARCH_MIN_LENGTH) return [];
  const raw = await get<unknown>(
    `${BASE_URL}/stripe/customers?search=${encodeURIComponent(term)}`,
    { signal }
  );
  const record = isRecord(raw) ? raw : {};
  return collection(record.results ?? raw).map(normalizeCustomerSearchItem).filter(notNull);
};

/**
 * Points this organization's billing at a Stripe customer that already exists.
 *
 * **This is the switch.** From the moment it succeeds Stripe is the system of record: this system
 * raises no invoices for the organization and mails it nothing. Back it with a backfill so the
 * invoices Stripe already holds appear here.
 */
export const linkStripeCustomer = async (
  orgId: string,
  customerId: string
): Promise<BillingStripeCustomer> =>
  normalizeStripeCustomer(
    await post<unknown>(
      `${stripeOrgUrl(orgId)}/link`,
      { customerId: customerId.trim() },
      { timeout: WRITE_TIMEOUT_MS }
    )
  );

/**
 * Imports every invoice Stripe already holds for this organization's customer.
 *
 * Idempotent: pressing it twice updates the same rows rather than duplicating them, because the
 * mirror keys on Stripe's own invoice id. A customer with no invoices reports zero and is not an
 * error.
 */
export const backfillStripeInvoices = async (orgId: string): Promise<BillingStripeBackfill> =>
  normalizeBackfill(
    await post<unknown>(`${stripeOrgUrl(orgId)}/backfill`, undefined, {
      // A backfill walks every invoice at Stripe, so it is given longer than an ordinary write.
      timeout: 60000,
    }),
    orgId
  );

/**
 * Creates the Stripe customer, or links the one the plan already has. Safe to press twice.
 *
 * **Send only what was actually filled in.** Every field is optional and an omitted one means "use
 * what this system already knows" — the organization's name, the address its invoices already go
 * to. Sending a blank string instead would store a blank at Stripe, and an empty body is sent as no
 * body at all so an untouched form behaves exactly as the old bare button did.
 *
 * The response's `message` is the thing to put on screen: on an organization that already has a
 * live customer this call links to it and applies **none** of these details.
 */
export const ensureStripeCustomer = async (
  orgId: string,
  details?: CreateStripeCustomerRequest
): Promise<BillingStripeCustomer> =>
  normalizeStripeCustomer(
    await post<unknown>(
      `${stripeOrgUrl(orgId)}/customer`,
      details && Object.keys(details).length > 0 ? details : undefined,
      { timeout: WRITE_TIMEOUT_MS }
    )
  );

/**
 * Creates the Stripe subscription that raises this organization's invoices.
 *
 * **This is the step that makes the account actually bill.** A Stripe customer hands billing to
 * Stripe; until a subscription exists Stripe raises nothing on a schedule, and neither does this
 * system, so the organization owes nobody anything.
 *
 * With no request at all the plan supplies the amount, the interval and the currency, and
 * collection defaults to sending the invoice. Nothing is written unless Stripe returns a
 * subscription, so a refusal leaves the organization exactly as it was and the retry is pressing
 * the button again.
 */
export const createStripeSubscription = async (
  orgId: string,
  request?: CreateStripeSubscriptionRequest
): Promise<BillingStripeSubscription> =>
  normalizeSubscription(
    await post<unknown>(
      `${stripeOrgUrl(orgId)}/subscription`,
      request && Object.keys(request).length > 0 ? request : undefined,
      { timeout: WRITE_TIMEOUT_MS }
    ),
    orgId
  );

/**
 * The subscription as Stripe has it now, and the correction of the plan's cached copy.
 *
 * An organization with no subscription answers 200 saying so rather than 404 — `hasSubscription`
 * false is a state to render, not an error to report.
 */
export const getStripeSubscription = async (
  orgId: string,
  signal?: AbortSignal
): Promise<BillingStripeSubscription> =>
  normalizeSubscription(
    await get<unknown>(`${stripeOrgUrl(orgId)}/subscription`, { signal }),
    orgId
  );

/**
 * Stops the subscription.
 *
 * **`atPeriodEnd` is the difference between two very different acts** and is always passed
 * explicitly rather than left to the server's default: true lets the period the customer has
 * already been invoiced for run out, false ends billing now and forfeits the rest of it with no
 * refund. Either way the organization stays Stripe managed and its mirrored invoices are untouched.
 */
export const cancelStripeSubscription = async (
  orgId: string,
  atPeriodEnd: boolean
): Promise<BillingStripeSubscription> =>
  normalizeSubscription(
    await del<unknown>(
      `${stripeOrgUrl(orgId)}/subscription?atPeriodEnd=${atPeriodEnd ? 'true' : 'false'}`,
      { timeout: WRITE_TIMEOUT_MS }
    ),
    orgId
  );

/**
 * Starts attaching a card or a bank account.
 *
 * The method is **not** on file when this returns: it hands back the client secret a hosted Stripe
 * step confirms against. No card or account number is ever collected by this app.
 */
export const startStripeSetupIntent = async (
  orgId: string,
  kind: BillingStripeSetupKind
): Promise<BillingStripeSetupIntent> =>
  normalizeSetupIntent(
    await post<unknown>(`${stripeOrgUrl(orgId)}/setup-intent`, { kind }, {
      timeout: WRITE_TIMEOUT_MS,
    }),
    kind
  );

export const getStripePaymentMethod = async (
  orgId: string,
  signal?: AbortSignal
): Promise<BillingStripePaymentMethod> =>
  normalizePaymentMethod(
    await get<unknown>(`${stripeOrgUrl(orgId)}/payment-method`, { signal }),
    orgId
  );

/** Detaches the stored method at Stripe and clears it from the plan. */
export const removeStripePaymentMethod = async (
  orgId: string
): Promise<BillingStripePaymentMethod> =>
  normalizePaymentMethod(
    await del<unknown>(`${stripeOrgUrl(orgId)}/payment-method`, { timeout: WRITE_TIMEOUT_MS }),
    orgId
  );

/*
  There is deliberately no charge call here, and there is no endpoint behind one. Collection is
  Stripe's: it auto-charges the stored method, or the customer pays on the hosted invoice page whose
  url rides on the invoice. The setup-intent calls above remain, because Stripe needs a default
  method on file to auto-charge against.
*/
